import { setMerchantLiveUpdatesState } from 'actions/merchant'
import type { Auth } from 'reducers/auth'
import { getErrorCode } from 'utilities/getErrorCode'
import { isPlainObject } from 'utilities/isPlainObject'
import { api } from './index'
import type {
  LiveUpdatesConfiguration,
  LiveUpdatesConfigurationDetail,
  LiveUpdatesConfigurationDetailWire,
  LiveUpdatesConfigurations,
  LiveUpdatesConfigurationsWire,
  LiveUpdatesContentField,
  LiveUpdatesDisableErrorKind,
  LiveUpdatesLocation,
  LiveUpdatesLocations,
  LiveUpdatesLocationsParams,
  LiveUpdatesLocationsWire,
  LiveUpdatesLocationWire,
  LiveUpdatesProvider,
  LiveUpdatesPublishBodyWire,
  LiveUpdatesPublishError,
  LiveUpdatesPublishErrorKey,
  LiveUpdatesPublishParams,
  LiveUpdatesPublishStatus,
  LiveUpdatesPublishStatusWire,
  LiveUpdatesStack,
  LiveUpdatesStatus,
  LiveUpdatesStatusWire,
  LiveUpdatesSummary,
  LiveUpdatesSummaryWire,
} from './liveUpdates.types'
import { isFetchBaseQueryError } from './utils'

// Resolved via indexed access on the exported wire types rather than importing
// the unexported LiveUpdates*Wire nested shapes directly (they're intentionally
// private to liveUpdates.types.ts) — keeps this file's helper transforms in
// lockstep with the contract without widening its public surface.
type LiveUpdatesConfigurationRowWire =
  LiveUpdatesConfigurationsWire['configurations'][number]
type LiveUpdatesStackWire = LiveUpdatesConfigurationRowWire['stack']
type LiveUpdatesProviderWire = LiveUpdatesStackWire['ordering_provider']
type LiveUpdatesContentFieldWire =
  LiveUpdatesStatusWire['content']['status_title']

const transformProvider = (
  provider: LiveUpdatesProviderWire | undefined
): LiveUpdatesProvider | null =>
  provider ? { key: provider.key, displayName: provider.display_name } : null

const transformStack = (
  stack: LiveUpdatesStackWire | undefined
): LiveUpdatesStack => ({
  orderingProvider: transformProvider(stack?.ordering_provider),
  deliveryProvider: transformProvider(stack?.delivery_provider),
  statusProvider: transformProvider(stack?.status_provider),
})

const transformConfigurationRow = (
  row: LiveUpdatesConfigurationRowWire
): LiveUpdatesConfiguration => ({
  id: row?.id ?? '',
  label: row?.label ?? '',
  stack: transformStack(row?.stack),
  locationsCount: Number(row?.locations_count) || 0,
  // Strict `=== true` (not Boolean()) so malformed wire data fails CLOSED:
  // Boolean('false') is true, which would silently open a locked/unsupported
  // configuration. An unexpected value coerces to the safe default (false).
  configurable: row?.configurable === true,
  deliveryConfigurable: row?.delivery_configurable === true,
  customized: row?.customized === true,
  lastPublishedAt: row?.last_published_at ?? null,
  refreshedAt: row?.refreshed_at ?? null,
})

const transformContentField = (
  field: LiveUpdatesContentFieldWire | undefined
): LiveUpdatesContentField => ({
  value: field?.value ?? null,
  maxLength: Number(field?.max_length) || 0,
  editable: field?.editable === true,
  // Carry the API-supplied default copy through. Absent (the companion nexus
  // PR hasn't deployed yet) or explicitly null (no default fires, e.g. the
  // preparing push) both normalize to null — consumers render an empty string.
  default: field?.default ?? null,
})

const transformStatus = (status: LiveUpdatesStatusWire): LiveUpdatesStatus => ({
  status: status.status,
  stage: status.stage,
  section: status.section,
  applicable: status.applicable === true,
  required: status.required === true,
  enabled: status.enabled === true,
  source: status.source,
  channels: Array.isArray(status.channels) ? status.channels : [],
  triggerSource: {
    label: status.trigger_source?.label ?? '',
    detail: status.trigger_source?.detail ?? null,
  },
  timerEligible: status.timer_eligible === true,
  eventSupported: status.event_supported !== false,
  triggerMode: status.trigger_mode,
  timerOffset: status.timer_offset ?? null,
  timerOffsetValues: Array.isArray(status.timer_offset_values)
    ? status.timer_offset_values
    : [],
  timerDirection: status.timer_direction ?? null,
  content: {
    statusTitle: transformContentField(status.content?.status_title),
    pushMessageText: transformContentField(status.content?.push_message_text),
    homescreenText: transformContentField(status.content?.homescreen_text),
  },
})

const transformLocation = (
  location: LiveUpdatesLocationWire
): LiveUpdatesLocation => ({
  id: location?.id ?? '',
  name: location?.name ?? '',
  street: location?.street ?? '',
  city: location?.city ?? '',
  state: location?.state ?? '',
  zip: location?.zip ?? '',
})

// Pure wire→domain mappers, exported for direct unit testing (mirrors
// escalationContacts'/makeGoodLadder's transform* exports). Fail-soft by
// construction: a missing/malformed top-level key or list coerces to a sensible
// default (0/[]/null/'') and never throws — a throw would surface as a
// rejected query rather than degraded data.
export const transformLiveUpdatesSummary = (
  response: LiveUpdatesSummaryWire
): LiveUpdatesSummary => {
  const summary = response?.summary
  return {
    // Strict `=== true` so a malformed `eligible` (e.g. the string 'false')
    // fails CLOSED to ineligible rather than opening the page to a merchant
    // the backend didn't actually clear.
    eligible: summary?.eligible === true,
    configurationsCount: Number(summary?.configurations_count) || 0,
    flybuy: summary?.flybuy === true,
    warnings: Array.isArray(summary?.warnings) ? summary.warnings : [],
  }
}

export const transformLiveUpdatesConfigurations = (
  response: LiveUpdatesConfigurationsWire
): LiveUpdatesConfigurations => ({
  configurations: Array.isArray(response?.configurations)
    ? response.configurations.map(transformConfigurationRow)
    : [],
  unassignedLocationsCount: Number(response?.unassigned_locations_count) || 0,
})

export const transformLiveUpdatesConfigurationDetail = (
  response: LiveUpdatesConfigurationDetailWire
): LiveUpdatesConfigurationDetail => {
  const configuration = response?.configuration
  // base_version is load-bearing for write correctness — a record that can't
  // report its version must NOT be publishable with a fabricated one (a `|| 0`
  // would send base_version:0 and dead-end on the server). Guard on the raw
  // type BEFORE coercion: Number(null) / Number('') / Number(false) / Number([])
  // are all a finite 0, so an explicit null/''/malformed current_version (the
  // exact contract break this defends against) would otherwise fabricate a 0.
  // Only an actual finite number is trusted; anything else is null, which the
  // hook/scene treat as not-publishable and surface the load-failed state.
  const rawVersion = configuration?.current_version
  return {
    ...transformConfigurationRow(configuration),
    currentVersion:
      typeof rawVersion === 'number' && Number.isFinite(rawVersion)
        ? rawVersion
        : null,
    // A malformed element (null / non-object) is dropped rather than crashing
    // transformStatus (which reads status.status directly) — keeps the whole
    // transform fail-soft per this file's never-throws contract.
    statuses: Array.isArray(configuration?.statuses)
      ? configuration.statuses
          .filter((status): status is LiveUpdatesStatusWire =>
            isPlainObject(status)
          )
          .map(transformStatus)
      : [],
  }
}

export const transformLiveUpdatesLocations = (
  response: LiveUpdatesLocationsWire
): LiveUpdatesLocations => ({
  locations: Array.isArray(response?.locations)
    ? response.locations.map(transformLocation)
    : [],
  pagination: {
    totalPages: Number(response?.pagination?.total_pages) || 0,
    perPage: Number(response?.pagination?.per_page) || 0,
    currentPage: Number(response?.pagination?.current_page) || 0,
    totalCount: Number(response?.pagination?.total_count) || 0,
  },
})

// Builds the PUT body from only the fields the merchant actually edited —
// omitted LiveUpdatesPublishStatus keys must stay omitted on the wire (the
// backend leaves untouched fields alone; sending an explicit `undefined` key
// would serialize away via JSON.stringify anyway, but building the object key
// by key keeps the intent explicit and testable).
const buildPublishStatusWire = (
  status: LiveUpdatesPublishStatus
): LiveUpdatesPublishStatusWire => {
  const wire: LiveUpdatesPublishStatusWire = { status: status.status }
  if (status.enabled !== undefined) {
    wire.enabled = status.enabled
  }
  if (status.triggerMode !== undefined) {
    wire.trigger_mode = status.triggerMode
  }
  if (status.timerOffset !== undefined) {
    wire.timer_offset = status.timerOffset
  }
  if (status.statusTitle !== undefined) {
    wire.status_title = status.statusTitle
  }
  if (status.pushMessageText !== undefined) {
    wire.push_message_text = status.pushMessageText
  }
  if (status.homescreenText !== undefined) {
    wire.homescreen_text = status.homescreenText
  }
  return wire
}

export const buildPublishBody = (
  params: LiveUpdatesPublishParams
): LiveUpdatesPublishBodyWire => ({
  base_version: params.baseVersion,
  statuses: params.statuses.map(buildPublishStatusWire),
  ...(params.enableLiveUpdates ? { enable_live_updates: true as const } : {}),
})

// The keys a 400 envelope's errors.statuses map may legitimately carry — the 9
// canonical statuses plus the stack-level 'configuration' bucket. Any other key
// is treated as unrecognized (folded into the flat messages list) so a future
// server-side key is surfaced rather than silently dropped.
const PUBLISH_ERROR_KEYS: LiveUpdatesPublishErrorKey[] = [
  'placed',
  'preparing',
  'ready',
  'picked_up',
  'en_route',
  'arrived',
  'delivered',
  'canceled_by_restaurant',
  'canceled_by_delivery',
  'configuration',
]
const PUBLISH_ERROR_KEY_SET = new Set<string>(PUBLISH_ERROR_KEYS)

const stringMessages = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((message): message is string => typeof message === 'string')
    : []

// Narrows an unknown error `data` payload down to the 400/409/422 envelope
// fields without ever force-casting — each level is checked with a type
// predicate before the next property is read. Recognized status keys land in
// statusErrors; any unrecognized key's messages are folded into the flat
// messages array (prefixed with the raw key) so nothing the server sends is
// silently dropped.
const parsePublishErrorData = (
  data: unknown
): {
  statusErrors: Partial<Record<LiveUpdatesPublishErrorKey, string[]>>
  messages: string[]
} => {
  const errors =
    isPlainObject(data) && isPlainObject(data.errors) ? data.errors : {}

  const rawStatuses = isPlainObject(errors.statuses) ? errors.statuses : {}
  const statusErrors: Partial<Record<LiveUpdatesPublishErrorKey, string[]>> = {}
  const foldedMessages: string[] = []
  Object.keys(rawStatuses).forEach(key => {
    const messages = stringMessages(rawStatuses[key])
    if (key === 'configuration') {
      // Stack-level rejection (not tied to any status card) — fold into the flat
      // messages so the scene's top-level banner surfaces it verbatim. No card
      // matches the 'configuration' key, so filing it under statusErrors would
      // silently drop the server's explanation.
      messages.forEach(message => foldedMessages.push(message))
    } else if (PUBLISH_ERROR_KEY_SET.has(key)) {
      statusErrors[key as LiveUpdatesPublishErrorKey] = messages
    } else {
      messages.forEach(message => foldedMessages.push(`${key}: ${message}`))
    }
  })

  const messages = [...stringMessages(errors.messages), ...foldedMessages]

  return { statusErrors, messages }
}

const LIVE_UPDATES_DISABLED_CODE = 'live_updates_disabled'
const RECOVERY_AI_TURN_OFF_FAILED_CODE = 'recovery_ai_turn_off_failed'

// Normalizes a publish mutation's rejected error into the shape the scene
// consumes: 400 carries per-status validation messages, 409 is a stale
// base_version conflict, 422 is a stack-level rejection, anything else is
// unknown (network failure, 5xx, etc).
export const toLiveUpdatesPublishError = (
  error: unknown
): LiveUpdatesPublishError => {
  if (!isFetchBaseQueryError(error)) {
    return { kind: 'unknown', statusErrors: {}, messages: [] }
  }

  const { statusErrors, messages } = parsePublishErrorData(error.data)

  if (error.status === 400) {
    return { kind: 'invalid', statusErrors, messages }
  }
  if (error.status === 409) {
    return { kind: 'conflict', statusErrors: {}, messages }
  }
  if (error.status === 422) {
    if (getErrorCode(error.data) === LIVE_UPDATES_DISABLED_CODE) {
      return { kind: 'live_updates_disabled', statusErrors: {}, messages: [] }
    }
    return { kind: 'unprocessable', statusErrors: {}, messages }
  }
  return { kind: 'unknown', statusErrors: {}, messages: [] }
}

// Normalizes a disable mutation's rejected error: a 422 with the Recovery AI
// code is deterministic (the server rolled the whole disable back), anything
// else is unknown.
export const toLiveUpdatesDisableErrorKind = (
  error: unknown
): LiveUpdatesDisableErrorKind =>
  isFetchBaseQueryError(error) &&
  error.status === 422 &&
  getErrorCode(error.data) === RECOVERY_AI_TURN_OFF_FAILED_CODE
    ? 'recovery_ai_turn_off_failed'
    : 'unknown'

const currentMerchantId = (getState: () => unknown) =>
  (getState() as { auth: Auth }).auth.current_merchant_id

// FE consumer of the Live Updates (Order Status V1) merchant config resource
// (nexus PR #870). Every endpoint 404s when the `live-updates-merchant-config`
// LaunchDarkly flag is off, so callers must gate on the flag before fetching.
export const liveUpdatesApi = api.injectEndpoints({
  endpoints: builder => ({
    getLiveUpdatesSummary: builder.query<LiveUpdatesSummary, void>({
      query: () => 'live_updates/summary',
      transformResponse: transformLiveUpdatesSummary,
      providesTags: ['LiveUpdatesSummary'],
    }),

    getLiveUpdatesConfigurations: builder.query<
      LiveUpdatesConfigurations,
      void
    >({
      query: () => 'live_updates/configurations',
      transformResponse: transformLiveUpdatesConfigurations,
      providesTags: ['LiveUpdatesConfigurations'],
    }),

    getLiveUpdatesConfiguration: builder.query<
      LiveUpdatesConfigurationDetail,
      string
    >({
      query: id => `live_updates/configurations/${id}`,
      transformResponse: transformLiveUpdatesConfigurationDetail,
      providesTags: (result, error, id) => [
        { type: 'LiveUpdatesConfiguration', id },
      ],
    }),

    getLiveUpdatesLocations: builder.query<
      LiveUpdatesLocations,
      LiveUpdatesLocationsParams
    >({
      query: ({ id, page, perPage, query }) => ({
        url: `live_updates/configurations/${id}/locations`,
        params: {
          query,
          page,
          per_page: perPage,
        },
      }),
      transformResponse: transformLiveUpdatesLocations,
      providesTags: (result, error, { id }) => [
        { type: 'LiveUpdatesLocations', id },
      ],
    }),

    publishLiveUpdatesConfiguration: builder.mutation<
      LiveUpdatesConfigurationDetail,
      LiveUpdatesPublishParams
    >({
      query: params => ({
        url: `live_updates/configurations/${params.id}`,
        method: 'PUT',
        body: buildPublishBody(params),
      }),
      transformResponse: transformLiveUpdatesConfigurationDetail,
      // On success, write the fresh detail (new current_version + statuses)
      // straight into the cache so the scene's edit buffer can clear WITHOUT a
      // stale-cache revert flash or a stale-base_version 409 on the next publish
      // — the PUT response already is the new truth, so a tag-invalidation
      // refetch is unnecessary here. On failure, the cache (and its base_version)
      // is left untouched so a 409 stays a conflict until the merchant reloads.
      async onQueryStarted({ id }, { dispatch, getState, queryFulfilled }) {
        // Capture before awaiting: the user can switch merchants mid-request.
        const merchantId = currentMerchantId(getState)
        try {
          const { data } = await queryFulfilled
          dispatch(
            liveUpdatesApi.util.upsertQueryData(
              'getLiveUpdatesConfiguration',
              id,
              data
            )
          )
          if (merchantId != null) {
            dispatch(setMerchantLiveUpdatesState(merchantId, 'enabled'))
          }
        } catch (rejection) {
          // A 422 live_updates_disabled means this client is stale: record the
          // disabled state.
          const error = isPlainObject(rejection) ? rejection.error : undefined
          if (
            merchantId != null &&
            toLiveUpdatesPublishError(error).kind === 'live_updates_disabled'
          ) {
            dispatch(setMerchantLiveUpdatesState(merchantId, 'disabled'))
          }
        }
      },
      // Never invalidate on a REJECTED publish: RTK's invalidation middleware
      // fires on rejected mutations too, so an unguarded callback would refetch
      // the detail on a 409 and silently refresh the optimistic-lock token,
      // letting the very next click bypass the conflict. The detail itself is
      // refreshed via upsertQueryData above, so only the list + summary need
      // invalidating, and only on success.
      invalidatesTags: (result, error) =>
        error ? [] : ['LiveUpdatesConfigurations', 'LiveUpdatesSummary'],
    }),

    disableLiveUpdates: builder.mutation<void, void>({
      query: () => ({
        url: 'live_updates/disable',
        method: 'PUT',
      }),
      async onQueryStarted(_arg, { dispatch, getState, queryFulfilled }) {
        // Capture before awaiting: the user can switch merchants mid-request.
        const merchantId = currentMerchantId(getState)
        try {
          await queryFulfilled
          if (merchantId != null) {
            dispatch(setMerchantLiveUpdatesState(merchantId, 'disabled'))
          }
        } catch {}
      },
      // The server turns RecoveryAI off inside the disable, so its cached
      // config and status must refetch.
      invalidatesTags: (result, error) =>
        error
          ? []
          : [
              'LiveUpdatesSummary',
              'LiveUpdatesConfigurations',
              'RecoveryConfig',
              'RecoveryStatus',
              'RecoveryActivation',
            ],
    }),
  }),
})

export const {
  useGetLiveUpdatesSummaryQuery,
  useGetLiveUpdatesConfigurationsQuery,
  useGetLiveUpdatesConfigurationQuery,
  useGetLiveUpdatesLocationsQuery,
  usePublishLiveUpdatesConfigurationMutation,
  useDisableLiveUpdatesMutation,
} = liveUpdatesApi
